import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBold,
  FaBullhorn,
  FaClock,
  FaEdit,
  FaLink,
  FaPaperclip,
  FaPlus,
  FaSearch,
  FaTrash,
} from "react-icons/fa";
import Modal from "../components/Modal";
import api from "../api";
import { useAuth } from "../AuthContext";
import { normalizeMuralLink, sanitizeMuralHtml, sanitizeMuralNotice } from "../security/muralHtml";
import {
  hasPermission,
  isAdmin,
  MURAL_CREATE_PERMISSION,
  MURAL_UPDATE_PERMISSION,
  MURAL_DELETE_PERMISSION,
} from "../utils/permissions";
import "./Mural.css";

const emptyForm = {
  title: "",
  type: "comunicado",
  messageHtml: "",
  textColor: "#c62828",
  highlightColor: "#fff59d",
  borderColor: "",
  eventDateTime: "",
  coverPhoto: null,
  coverPhotoName: "",
  attachments: [],
  selectedSectorIds: [],
  selectedGroupIds: [],
  selectedUserIds: [],
  priority: "media",
  validUntil: "",
};

const priorityLabels = {
  alta: "Alta",
  media: "Média",
  baixa: "Baixa",
};

const noticeTypeLabels = {
  comunicado: "Comunicado",
  evento: "Evento",
};

const formatDateTime = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  });
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const stripHtml = (html = "") => {
  if (typeof document === "undefined") {
    return html.replace(/<[^>]*>/g, " ").trim();
  }
  const temp = document.createElement("div");
  temp.innerHTML = sanitizeMuralHtml(html);
  return (temp.textContent || temp.innerText || "").trim();
};

export default function Mural() {
  const { user } = useAuth();
  const [notices, setNotices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [groups, setGroups] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [users, setUsers] = useState([]);
  const [formState, setFormState] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [audienceError, setAudienceError] = useState("");
  const [audienceReloadKey, setAudienceReloadKey] = useState(0);
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const editorRef = useRef(null);

  const isMasterUser = Boolean(user?.isMaster);
  const canCreate = isMasterUser || isAdmin(user) || hasPermission(user, MURAL_CREATE_PERMISSION);
  const canUpdate = isMasterUser || isAdmin(user) || hasPermission(user, MURAL_UPDATE_PERMISSION);
  const canDelete = isMasterUser || isAdmin(user) || hasPermission(user, MURAL_DELETE_PERMISSION);
  const canLoadAudience = canCreate || canUpdate;

  useEffect(() => {
    if (!canLoadAudience) {
      setGroups([]);
      setSectors([]);
      setUsers([]);
      setAudienceLoading(false);
      setAudienceError("");
      return;
    }

    let active = true;
    const loadAudienceSources = async () => {
      setAudienceLoading(true);
      setAudienceError("");
      try {
        const response = await api.get("/notice-board-posts/audience");
        if (!active) return;
        const data = response?.data || {};
        setGroups(Array.isArray(data.groups) ? data.groups : []);
        setSectors(Array.isArray(data.sectors) ? data.sectors : []);
        setUsers(Array.isArray(data.users) ? data.users : []);
      } catch (error) {
        if (!active) return;
        setGroups([]);
        setSectors([]);
        setUsers([]);
        setAudienceError(
          error?.response?.data?.error || "Não foi possível carregar os destinatários do mural."
        );
      } finally {
        if (active) setAudienceLoading(false);
      }
    };

    loadAudienceSources();
    return () => {
      active = false;
    };
  }, [canLoadAudience, audienceReloadKey]);

  useEffect(() => {
    const loadNotices = async () => {
      try {
        const response = await api.get("/notice-board-posts");
        setNotices(Array.isArray(response.data) ? response.data.map(sanitizeMuralNotice) : []);
      } catch {
        setNotices([]);
      }
    };

    loadNotices();
  }, []);

  const sectorOptions = useMemo(
    () => sectors.map((sector) => ({ value: String(sector.id), label: sector.name || `Setor ${sector.id}` })),
    [sectors]
  );
  const groupOptions = useMemo(
    () => groups.map((group) => ({ value: String(group.id), label: group.name || `Grupo ${group.id}` })),
    [groups]
  );
  const userOptions = useMemo(
    () => users.map((person) => ({ value: String(person.id), label: person.name || person.email || `Usuário ${person.id}` })),
    [users]
  );

  const visibleNotices = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const userId = user?.id ? String(user.id) : "";
    const userGroupId = user?.group?.id ? String(user.group.id) : "";
    const userSectorId = user?.sector?.id ? String(user.sector.id) : "";

    return notices
      .filter((notice) => {
        if (notice?.createdById && user?.id && Number(notice.createdById) === Number(user.id)) {
          return true;
        }
        const recipients = Array.isArray(notice.recipients) ? notice.recipients : [];
        const canSeeNotice = recipients.some((recipient) => {
          if (!recipient?.type || !recipient?.id) return false;
          const recipientId = String(recipient.id);
          if (recipient.type === "particular") return recipientId === userId;
          if (recipient.type === "grupo") return recipientId === userGroupId;
          if (recipient.type === "setor") return recipientId === userSectorId;
          return false;
        });
        if (!canSeeNotice) return false;

        if (!normalizedSearch) return true;
        const recipientNames = recipients.map((recipient) => recipient?.name).join(" ");
        return [notice.title, stripHtml(notice.messageHtml || ""), recipientNames, notice.type]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(normalizedSearch));
      })
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [notices, searchTerm, user]);

  const openCreateModal = () => {
    if (!canCreate) return;
    setEditingId(null);
    setFormError("");
    setFormState(emptyForm);
    setIsModalOpen(true);
  };

  const openEditModal = (notice) => {
    if (!canUpdate) return;
    setEditingId(notice.id);
    setFormError("");
    setFormState({
      title: notice.title || "",
      type: notice.type || "comunicado",
      messageHtml: notice.messageHtml || "",
      textColor: notice.textColor || "#c62828",
      highlightColor: notice.highlightColor || "#fff59d",
      borderColor: notice.borderColor || "",
      eventDateTime: notice.eventDateTime || "",
      coverPhoto: notice.coverPhoto || null,
      coverPhotoName: notice.coverPhotoName || "",
      attachments: Array.isArray(notice.attachments) ? notice.attachments : [],
      selectedSectorIds: (notice.recipients || [])
        .filter((recipient) => recipient.type === "setor")
        .map((recipient) => String(recipient.id)),
      selectedGroupIds: (notice.recipients || [])
        .filter((recipient) => recipient.type === "grupo")
        .map((recipient) => String(recipient.id)),
      selectedUserIds: (notice.recipients || [])
        .filter((recipient) => recipient.type === "particular")
        .map((recipient) => String(recipient.id)),
      priority: notice.priority || "media",
      validUntil: notice.validUntil || "",
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormError("");
    setFormState(emptyForm);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormError("");
    setFormState((prev) => {
      if (name === "type" && value !== "evento") {
        return { ...prev, type: value, eventDateTime: "" };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleRecipientsChange = (event, fieldName) => {
    const selectedValues = Array.from(event.target.selectedOptions || []).map((option) => option.value);
    setFormError("");
    setFormState((prev) => ({ ...prev, [fieldName]: selectedValues }));
  };

  const handleEditorInput = () => {
    const nextHtml = editorRef.current?.innerHTML || "";
    setFormError("");
    setFormState((prev) => ({ ...prev, messageHtml: nextHtml }));
  };

  const applyTextCommand = (command, value = null) => {
    if (!editorRef.current) return;
    let commandValue = value;
    if (command === "createLink") {
      if (!value) return;
      commandValue = normalizeMuralLink(value);
      if (!commandValue) {
        window.alert("Use um endereço completo iniciado por http:// ou https://.");
        return;
      }
    }
    editorRef.current.focus();
    document.execCommand(command, false, commandValue);
    handleEditorInput();
  };

  const handleEditorPaste = (event) => {
    const html = event.clipboardData?.getData("text/html");
    if (!html) return;
    event.preventDefault();
    document.execCommand("insertHTML", false, sanitizeMuralHtml(html));
    handleEditorInput();
  };

  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setFormState((prev) => ({
        ...prev,
        coverPhoto: dataUrl,
        coverPhotoName: file.name,
      }));
    } catch {
      // ignore invalid file reads
    } finally {
      event.target.value = "";
    }
  };

  const handleAttachmentsChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    try {
      const converted = await Promise.all(
        files.map(async (file) => ({
          id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          name: file.name,
          mimeType: file.type || "application/octet-stream",
          size: file.size || 0,
          dataUrl: await fileToDataUrl(file),
        }))
      );
      setFormState((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...converted],
      }));
    } catch {
      // ignore invalid attachments
    } finally {
      event.target.value = "";
    }
  };

  const removeAttachment = (id) => {
    setFormState((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((attachment) => attachment.id !== id),
    }));
  };

  const buildRecipients = () => {
    const recipients = [];

    formState.selectedSectorIds.forEach((id) => {
      const match = sectorOptions.find((item) => item.value === String(id));
      recipients.push({ type: "setor", id: String(id), ...(match?.label ? { name: match.label } : {}) });
    });

    formState.selectedGroupIds.forEach((id) => {
      const match = groupOptions.find((item) => item.value === String(id));
      recipients.push({ type: "grupo", id: String(id), ...(match?.label ? { name: match.label } : {}) });
    });

    formState.selectedUserIds.forEach((id) => {
      const match = userOptions.find((item) => item.value === String(id));
      recipients.push({ type: "particular", id: String(id), ...(match?.label ? { name: match.label } : {}) });
    });

    return recipients;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const title = formState.title.trim();
    const messageHtml = sanitizeMuralHtml(editorRef.current?.innerHTML || formState.messageHtml || "");
    const recipients = buildRecipients();

    if (!title) {
      setFormError("Informe o título do aviso.");
      return;
    }
    if (!stripHtml(messageHtml)) {
      setFormError("Informe o conteúdo do aviso.");
      return;
    }
    if (audienceLoading) {
      setFormError("Aguarde o carregamento dos destinatários.");
      return;
    }
    if (audienceError) {
      setFormError("Recarregue os destinatários antes de publicar.");
      return;
    }
    if (recipients.length === 0) {
      setFormError("Selecione ao menos um setor, grupo ou usuário destinatário.");
      return;
    }

    setFormError("");
    setIsSubmitting(true);
    const nowIso = new Date().toISOString();

    const payload = {
      title,
      type: formState.type,
      messageHtml,
      textColor: formState.textColor,
      highlightColor: formState.highlightColor,
      borderColor: formState.borderColor,
      eventDateTime: formState.type === "evento" ? formState.eventDateTime : null,
      coverPhoto: formState.coverPhoto || null,
      coverPhotoName: formState.coverPhotoName || "",
      attachments: Array.isArray(formState.attachments) ? formState.attachments : [],
      recipients,
      priority: formState.priority,
      validUntil: formState.validUntil || null,
      editedAt: nowIso,
    };

    try {
      if (editingId) {
        const response = await api.put(`/notice-board-posts/${editingId}`, payload);
        setNotices((prev) =>
          prev.map((notice) =>
            notice.id === editingId
              ? { ...sanitizeMuralNotice(response.data), editedAt: nowIso }
              : notice
          )
        );
      } else {
        const response = await api.post("/notice-board-posts", payload);
        setNotices((prev) => [sanitizeMuralNotice(response.data), ...prev]);
      }
      closeModal();
    } catch (error) {
      const message = error?.response?.data?.error || "Não foi possível publicar o aviso.";
      setFormError(message);
      window.alert(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!canDelete) return;
    if (!window.confirm("Deseja excluir este aviso?")) return;
    try {
      await api.delete(`/notice-board-posts/${id}`);
      setNotices((prev) => prev.filter((notice) => notice.id !== id));
    } catch (error) {
      const message = error?.response?.data?.error || "Não foi possível excluir o aviso.";
      window.alert(message);
    }
  };

  useEffect(() => {
    if (!isModalOpen || !editorRef.current) return;
    const html = sanitizeMuralHtml(formState.messageHtml || "");
    if (editorRef.current.innerHTML !== html) {
      editorRef.current.innerHTML = html;
    }
  }, [isModalOpen, formState.messageHtml]);

  return (
    <div className="mural-page">
      <div className="mural-toolbar">
        <div className="mural-search">
          <FaSearch />
          <input
            type="search"
            placeholder="Buscar aviso por título, mensagem ou público..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        {canCreate ? (
          <button type="button" className="mural-manage-button" onClick={openCreateModal}>
            <FaPlus /> Novo aviso
          </button>
        ) : null}
      </div>

      <div className="mural-notices-only">
        {visibleNotices.length === 0 ? (
          <div className="mural-empty-state">
            <FaBullhorn />
            <p>Nenhum aviso publicado.</p>
          </div>
        ) : (
          visibleNotices.map((notice) => (
            <article
              key={notice.id}
              className={`mural-notice-card priority-${notice.priority || "media"}`}
              style={notice.borderColor ? { borderLeftColor: notice.borderColor } : undefined}
            >
              <div className="mural-notice-header">
                <h3>
                  <span className="notice-type-badge">{noticeTypeLabels[notice.type] || "Comunicado"}</span>
                  {notice.title}
                </h3>
                <span className="priority-badge">{priorityLabels[notice.priority] || "Média"}</span>
              </div>
              {notice.type === "evento" && notice.eventDateTime ? (
                <div className="mural-event-date">Data do evento: {formatDateTime(notice.eventDateTime)}</div>
              ) : null}
              {notice.coverPhoto ? (
                <div className="mural-cover-wrapper">
                  <img src={notice.coverPhoto} alt={notice.coverPhotoName || "Imagem do aviso"} className="mural-cover" />
                </div>
              ) : null}
              <div
                className="mural-message-content"
                dangerouslySetInnerHTML={{ __html: sanitizeMuralHtml(notice.messageHtml || "<p></p>") }}
              />
              <div className="mural-notice-footer">
                <span>
                  <FaClock /> {formatDateTime(notice.createdAt)}
                </span>
                {notice.editedAt ? <span className="edited-at">Editado em {formatDateTime(notice.editedAt)}</span> : null}
                {notice.validUntil ? <span>Válido até {new Date(notice.validUntil).toLocaleDateString("pt-BR")}</span> : null}
              </div>
              {Array.isArray(notice.attachments) && notice.attachments.length > 0 ? (
                <div className="mural-attachments">
                  {notice.attachments.map((attachment) => (
                    <a
                      key={attachment.id}
                      href={attachment.dataUrl}
                      download={attachment.name}
                      className="mural-attachment-link"
                    >
                      <FaPaperclip /> {attachment.name}
                    </a>
                  ))}
                </div>
              ) : null}
              {(canUpdate || canDelete) && (
                <div className="mural-notice-actions">
                  {canUpdate ? (
                    <button type="button" onClick={() => openEditModal(notice)}>
                      <FaEdit /> Editar
                    </button>
                  ) : null}
                  {canDelete ? (
                    <button type="button" className="danger" onClick={() => handleDelete(notice.id)}>
                      <FaTrash /> Excluir
                    </button>
                  ) : null}
                </div>
              )}
            </article>
          ))
        )}
      </div>

      <Modal open={isModalOpen} onClose={closeModal} size="max-w-2xl">
        <section className="mural-modal-content">
          <h2>{editingId ? "Editar aviso" : "Novo aviso"}</h2>
          <form onSubmit={handleSubmit} className="mural-compose-form">
            <label>
              Título
              <input
                name="title"
                value={formState.title}
                onChange={handleChange}
                placeholder="Ex.: Comunicado importante"
                required
              />
            </label>

            <div className="mural-form-row">
              <label>
                Tipo
                <select name="type" value={formState.type} onChange={handleChange}>
                  <option value="comunicado">Comunicado</option>
                  <option value="evento">Evento</option>
                </select>
              </label>
              {formState.type === "evento" ? (
                <label>
                  Data e hora do evento (opcional)
                  <input
                    type="datetime-local"
                    name="eventDateTime"
                    value={formState.eventDateTime}
                    onChange={handleChange}
                  />
                </label>
              ) : (
                <div className="mural-spacer" />
              )}
            </div>

            <label>
              Conteúdo
              <div className="mural-editor-toolbar">
                <button type="button" onClick={() => applyTextCommand("bold")} title="Negrito">
                  <FaBold />
                </button>
                <button type="button" onClick={() => applyTextCommand("createLink", window.prompt("Informe a URL", "https://") || "")} title="Inserir link">
                  <FaLink />
                </button>
              </div>
              <div
                ref={editorRef}
                className="mural-rich-editor"
                contentEditable
                suppressContentEditableWarning
                aria-label="Conteúdo do aviso"
                onInput={handleEditorInput}
                onPaste={handleEditorPaste}
                data-placeholder="Digite aqui o aviso"
              />
            </label>

            {audienceLoading ? (
              <p className="mural-form-status" role="status">Carregando destinatários...</p>
            ) : null}
            {audienceError ? (
              <div className="mural-form-error">
                <span role="alert">{audienceError}</span>
                <button type="button" onClick={() => setAudienceReloadKey((value) => value + 1)}>
                  Tentar carregar novamente
                </button>
              </div>
            ) : null}

            <div className="mural-form-row">
              <label>
                Setores (multi seleção)
                <select
                  multiple
                  value={formState.selectedSectorIds}
                  onChange={(event) => handleRecipientsChange(event, "selectedSectorIds")}
                  className="mural-multi-select"
                  disabled={audienceLoading || Boolean(audienceError)}
                >
                  {sectorOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Prioridade
                <select name="priority" value={formState.priority} onChange={handleChange}>
                  <option value="alta">Alta</option>
                  <option value="media">Média</option>
                  <option value="baixa">Baixa</option>
                </select>
              </label>
              <label>
                Cor da borda do aviso
                <input type="color" name="borderColor" value={formState.borderColor || "#f2994a"} onChange={handleChange} />
              </label>
            </div>

            <div className="mural-form-row">
              <label>
                Grupos (multi seleção)
                <select
                  multiple
                  value={formState.selectedGroupIds}
                  onChange={(event) => handleRecipientsChange(event, "selectedGroupIds")}
                  className="mural-multi-select"
                  disabled={audienceLoading || Boolean(audienceError)}
                >
                  {groupOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Usuários (multi seleção)
                <select
                  multiple
                  value={formState.selectedUserIds}
                  onChange={(event) => handleRecipientsChange(event, "selectedUserIds")}
                  className="mural-multi-select"
                  disabled={audienceLoading || Boolean(audienceError)}
                >
                  {userOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label>
              Foto (opcional)
              <input type="file" accept="image/*" onChange={handlePhotoChange} />
            </label>

            {formState.coverPhoto ? (
              <div className="mural-upload-preview">
                <img src={formState.coverPhoto} alt={formState.coverPhotoName || "Pré-visualização"} />
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setFormState((prev) => ({ ...prev, coverPhoto: null, coverPhotoName: "" }))}
                >
                  Remover foto
                </button>
              </div>
            ) : null}

            <label>
              Anexos (opcional)
              <input type="file" multiple onChange={handleAttachmentsChange} />
            </label>

            {formState.attachments.length ? (
              <div className="mural-attachment-list">
                {formState.attachments.map((attachment) => (
                  <div key={attachment.id} className="mural-attachment-item">
                    <span>
                      <FaPaperclip /> {attachment.name}
                    </span>
                    <button type="button" className="secondary" onClick={() => removeAttachment(attachment.id)}>
                      Remover
                    </button>
                  </div>
                ))}
              </div>
            ) : null}

            <label>
              Validade (opcional)
              <input type="date" name="validUntil" value={formState.validUntil} onChange={handleChange} />
            </label>

            {formError ? (
              <p role="alert" className="mural-form-error">{formError}</p>
            ) : null}

            <div className="mural-form-actions">
              <button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? (editingId ? "Salvando..." : "Publicando...")
                  : (editingId ? "Salvar alterações" : "Publicar aviso")}
              </button>
              <button type="button" className="secondary" onClick={closeModal}>
                Cancelar
              </button>
            </div>
          </form>
        </section>
      </Modal>
    </div>
  );
}
